// @ts-check
// Relações de uma nota ou tarefa (ActivityTargetsInlineCell do Twenty, doc 10, seção 6.5): chips dos registros
// vinculados (pessoas, cards, famílias e os demais objetos com atividades) e edição com busca em todos eles. Marcar cria
// a junção noteTarget/taskTarget, desmarcar remove; o motor exige edição do registro alvo.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { createRecord, mutationCounterState } from '../../data/record-store.js';
import { findManyRecords, searchRecords } from '../../data/rest.js';
import { api } from '../../data/api-client.js';
import { findTargetIdFieldName, toActivityTargetItems } from '../../lib/activity-target-items.js';
import { canUpdateObjectRecords } from '../../lib/permissions.js';
import { RecordChip } from '../../ui/chip.js';
import { Icon } from '../../ui/icon.js';
import { Avatar } from '../../ui/avatar.js';
import { Dropdown, DropdownMenuItems, DropdownMenuSearchInput, DropdownMenuSectionLabel, MenuItem } from '../../ui/dropdown.js';
import { useDebouncedValue } from '../../ui/hooks.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/** @typedef {import('../../lib/activity-target-items.js').ActivityTargetItem} ActivityTargetItem */

/**
 * @param {{ activityNameSingular: 'task' | 'note', activityId: string, isReadOnly?: boolean, showLabel?: boolean }} props
 */
export function ActivityTargetsCell({ activityNameSingular, activityId, isReadOnly = false, showLabel = true }) {
  const junctionObject = objectsByNameSingular.value.get(`${activityNameSingular}Target`);
  const junctionFields = junctionObject?.fields ?? [];
  const activityIdFieldName = `${activityNameSingular}Id`;
  const anchorRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [items, setItems] = useState(/** @type {ActivityTargetItem[] | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText, 250);
  const [results, setResults] = useState(/** @type {any[]} */ ([]));
  const [busyKey, setBusyKey] = useState(/** @type {string | null} */ (null));

  const reload = () => {
    if (!junctionObject) return;
    findManyRecords(junctionObject, { filter: { field: activityIdFieldName, operator: 'eq', value: activityId }, limit: 100, depth: 1 })
      .then((result) => setItems(toActivityTargetItems(/** @type {any} */ (junctionFields), result.records)))
      .catch((error) => {
        setItems([]);
        showErrorSnackbar(error);
      });
  };
  useEffect(reload, [activityId, junctionObject?.id]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let isCancelled = false;
    const linkable = [...objectsByNameSingular.value.values()]
      .filter((object) => !object.isSystem && findTargetIdFieldName(/** @type {any} */ (junctionFields), object.nameSingular))
      .map((object) => object.nameSingular);
    searchRecords(debouncedSearch, { limit: 20, includedObjectNameSingulars: linkable })
      .then((found) => {
        if (!isCancelled) setResults(found);
      })
      .catch(() => {
        if (!isCancelled) setResults([]);
      });
    return () => {
      isCancelled = true;
    };
  }, [isOpen, debouncedSearch]);

  if (!junctionObject) return null;
  const canEdit = !isReadOnly && canUpdateObjectRecords(objectsByNameSingular.value.get(activityNameSingular));

  /** @param {string} objectNameSingular @param {string} recordId */
  const toggle = async (objectNameSingular, recordId) => {
    const key = `${objectNameSingular}:${recordId}`;
    const existing = items?.find((item) => item.objectNameSingular === objectNameSingular && item.recordId === recordId);
    const idFieldName = findTargetIdFieldName(/** @type {any} */ (junctionFields), objectNameSingular);
    if (!idFieldName) return;
    setBusyKey(key);
    try {
      if (existing) await api.delete(`/rest/${junctionObject.namePlural}/${existing.junctionId}`);
      else await createRecord(junctionObject, { [activityIdFieldName]: activityId, [idFieldName]: recordId });
      mutationCounterState.value += 1;
      reload();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setBusyKey(null);
    }
  };

  const selectedKeys = new Set((items ?? []).map((item) => `${item.objectNameSingular}:${item.recordId}`));
  return html`<div class="inline-cell activity-targets-cell" data-readonly=${canEdit ? undefined : 'true'} ref=${anchorRef}>
    ${showLabel
      ? html`<div class="inline-cell-label">
          <${Icon} name="IconArrowUpRight" size=${16} stroke=${1.6} />
          <span class="inline-cell-label-text">${t('recordPage.activityTargets.label')}</span>
        </div>`
      : null}
    <div class="inline-cell-value" onClick=${(/** @type {MouseEvent} */ event) => {
      if (/** @type {HTMLElement} */ (event.target).closest('a') || !canEdit) return;
      setIsOpen(true);
    }}>
      <div class="inline-cell-display activity-targets-chips" data-testid="activity-targets">
        ${items === null
          ? null
          : items.length === 0
            ? html`<span class="field-display-empty">${t('recordPage.activityTargets.empty')}</span>`
            : items.map((item) => html`<${RecordChip} key=${`${item.objectNameSingular}:${item.recordId}`}
                objectMetadata=${objectsByNameSingular.value.get(item.objectNameSingular)} record=${item.record} />`)}
      </div>
    </div>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${300} placement="bottom-start">
      <${DropdownMenuSearchInput} value=${searchText} onChange=${setSearchText} placeholder=${t('recordPage.activityTargets.search')} />
      <${DropdownMenuItems} hasMaxHeight=${true}>
        ${(items ?? []).length > 0 ? html`<${DropdownMenuSectionLabel} label=${t('recordPage.activityTargets.linked')} />` : null}
        ${(items ?? []).map((item) => {
          const object = objectsByNameSingular.value.get(item.objectNameSingular);
          return html`<${MenuItem} key=${`linked-${item.junctionId}`} multiSelect=${true} selected=${true}
            disabled=${busyKey !== null}
            text=${html`<${RecordChip} objectMetadata=${object} record=${item.record} variant="transparent" disableLink=${true} />`}
            contextualText=${object ? translateMetadataLabel(object.labelSingular) : ''}
            onClick=${() => toggle(item.objectNameSingular, item.recordId)} />`;
        })}
        ${results.filter((result) => !selectedKeys.has(`${result.objectNameSingular}:${result.recordId}`)).length > 0
          ? html`<${DropdownMenuSectionLabel} label=${t('recordPage.activityTargets.results')} />`
          : null}
        ${results
          .filter((result) => !selectedKeys.has(`${result.objectNameSingular}:${result.recordId}`))
          .map((result) => html`<${MenuItem} key=${`result-${result.objectNameSingular}-${result.recordId}`} multiSelect=${true} selected=${false}
            disabled=${busyKey !== null}
            leftComponent=${html`<${Avatar} placeholder=${result.label || '-'} seed=${result.recordId} size="sm" avatarUrl=${result.imageUrl ?? null} />`}
            text=${result.label || t('record.untitled')}
            contextualText=${translateMetadataLabel(result.objectLabelSingular ?? '')}
            onClick=${() => toggle(result.objectNameSingular, result.recordId)} />`)}
      <//>
    <//>
  </div>`;
}
